import { Icon } from '@humanforce/mango-ui-react';
import type { ReactNode } from 'react';
import './PayRunProgressList.css';

export type PayRunProgressItemState = 'complete' | 'current' | 'rejected' | 'upcoming';

export interface PayRunProgressItem {
  id: string;
  label: string;
  state: PayRunProgressItemState;
  href?: string;
}

interface PayRunProgressListProps {
  label: string;
  items: PayRunProgressItem[];
  idPrefix: string;
  footer?: ReactNode;
  testId?: string;
}

const ITEM_ICON: Record<PayRunProgressItemState, { icon: 'status-positive' | 'clock' | 'status-critical' | 'status-pending'; className: string }> = {
  complete: { icon: 'status-positive', className: 'mgo-color-positive' },
  current: { icon: 'clock', className: 'mgo-color-info' },
  rejected: { icon: 'status-critical', className: 'mgo-color-critical' },
  upcoming: { icon: 'status-pending', className: '' },
};

const ITEM_STATE_DESCRIPTION: Record<PayRunProgressItemState, string> = {
  complete: 'completed',
  current: 'current',
  rejected: 'rejected',
  upcoming: 'not started',
};

export const PayRunProgressList = ({ label, items, idPrefix, footer, testId }: PayRunProgressListProps) => (
  <div className="pay-run-progress" data-testid={testId}>
    <p className="pay-run-progress__label" id={`${idPrefix}-label`}>{label}</p>
    <ul
      className="mgo-stack mgo-gap-0"
      aria-labelledby={`${idPrefix}-label`}
      style={{ listStyle: 'none', margin: 0, padding: 0 }}
    >
      {items.map((item, index) => {
        const { icon, className } = ITEM_ICON[item.state];
        const settled = index === items.length - 1 && item.state === 'complete';
        const rowClassName = `pay-run-progress__item pay-run-progress__item--${settled ? 'settled' : item.state}`;
        const content = (
          <>
            <span className={`pay-run-progress__item-icon mgo-text-s ${className}`.trim()} aria-hidden="true">
              <Icon name={icon} width="fixed" />
            </span>
            <span
              className={`pay-run-progress__item-label mgo-text-s${item.state === 'current' ? ' mgo-weight-strong' : ''}`}
            >
              {item.label}
            </span>
            {/* The glyph is decorative, so the state has to reach assistive tech as text. */}
            <span className="sr-only">{` — ${ITEM_STATE_DESCRIPTION[item.state]}`}</span>
          </>
        );
        return (
          <li key={item.id} className="pay-run-progress__item-wrapper">
            {item.href
              ? <a className={`${rowClassName} pay-run-progress__item--link`} href={item.href}>{content}</a>
              : <div className={rowClassName}>{content}</div>}
          </li>
        );
      })}
    </ul>
    {footer && <div className="pay-run-progress__footer">{footer}</div>}
  </div>
);
